import { useState } from "react";
import { useDispatch } from "react-redux";
import { HiOutlineClipboardCopy, HiOutlineCheck, HiOutlineExclamationCircle } from "react-icons/hi";
import Modal from "../shared/Modal";
import TextInput from "../shared/TextInput";
import Button from "../shared/Button";
import { sendInvitationAction } from "../../redux/actions/usersActions";

const InviteUserModal = ({ isOpen, onClose, organisationId }) => {
  const dispatch = useDispatch();
  const [email, setEmail] = useState("");
  const [role, setRole] = useState("viewer");
  const [error, setError] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [result, setResult] = useState(null); // { emailSent, emailError, link }
  const [copied, setCopied] = useState(false);

  const handleClose = () => {
    setEmail("");
    setRole("viewer");
    setError("");
    setResult(null);
    setCopied(false);
    onClose();
  };

  const handleSubmit = async () => {
    if (!email) {
      setError("Email is required");
      return;
    }
    setError("");
    setIsSubmitting(true);
    try {
      const data = await dispatch(sendInvitationAction(organisationId, email, role));
      setResult({
        emailSent: data?.emailSent !== false,
        emailError: data?.emailError || null,
        link: data?.link || null,
      });
    } catch (err) {
      setError(err.message || "Failed to send invitation");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleCopyLink = async () => {
    if (!result?.link) return;
    try {
      await navigator.clipboard.writeText(result.link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard API can fail in non-secure contexts; fall back silently
    }
  };

  const showResult = result !== null;

  return (
    <Modal
      isOpen={isOpen}
      onClose={handleClose}
      title={showResult ? "Invitation Created" : "Invite User"}
      size="sm"
      footer={
        showResult ? (
          <Button variant="primary" size="sm" onClick={handleClose}>
            Done
          </Button>
        ) : (
          <>
            <Button
              variant="secondary"
              size="sm"
              onClick={handleClose}
              disabled={isSubmitting}
            >
              Cancel
            </Button>
            <Button
              variant="primary"
              size="sm"
              onClick={handleSubmit}
              disabled={isSubmitting}
            >
              {isSubmitting ? "Sending..." : "Send Invite"}
            </Button>
          </>
        )
      }
    >
      {showResult ? (
        <div className="space-y-4">
          {result.emailSent ? (
            <div className="p-3 rounded-md border border-success bg-success/5">
              <p className="text-body-md text-ink">
                Invitation email sent to <strong>{email}</strong>.
              </p>
            </div>
          ) : (
            <div className="p-3 rounded-md border border-signature-coral bg-signature-coral/5">
              <div className="flex items-start gap-2">
                <HiOutlineExclamationCircle className="h-5 w-5 text-signature-coral shrink-0 mt-0.5" />
                <div>
                  <p className="text-body-md text-ink font-medium">
                    Invitation created, but the email could not be delivered.
                  </p>
                  {result.emailError && (
                    <p className="text-body-md text-muted mt-1">
                      {result.emailError}
                    </p>
                  )}
                  <p className="text-body-md text-muted mt-1">
                    Copy the link below and share it with the invitee directly.
                  </p>
                </div>
              </div>
            </div>
          )}

          {result.link && (
            <div>
              <label className="text-body-md font-medium text-ink block mb-1.5">
                Invitation link
              </label>
              <div className="flex gap-2">
                <input
                  readOnly
                  value={result.link}
                  className="flex-1 h-11 px-4 bg-surface-soft border border-hairline rounded-sm text-body-md text-ink"
                />
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={handleCopyLink}
                  leadingIcon={
                    copied ? (
                      <HiOutlineCheck className="h-4 w-4" />
                    ) : (
                      <HiOutlineClipboardCopy className="h-4 w-4" />
                    )
                  }
                >
                  {copied ? "Copied" : "Copy"}
                </Button>
              </div>
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <TextInput
            label="Email *"
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder="invitee@example.com"
            error={error}
          />
          <div className="flex flex-col gap-1.5">
            <label className="text-body-md font-medium text-ink">Role</label>
            <select
              value={role}
              onChange={(e) => setRole(e.target.value)}
              className="h-11 px-4 bg-canvas border border-hairline rounded-sm text-body-md text-ink focus:outline-none focus:border-info-border"
            >
              <option value="viewer">Viewer</option>
              <option value="admin">Admin</option>
            </select>
          </div>
        </div>
      )}
    </Modal>
  );
};

export default InviteUserModal;
